// The one thing on these pages that leaves the machine.
//
// Everything else here is a promise that nothing is sent anywhere. This is the
// deliberate exception: a signed-in visitor presses a button and the finished
// picture is uploaded to their own account, encrypted like any other upload,
// through /upload/web rather than through anything new. That route already
// knows about plan limits, quotas, folders and the account's upload rules, and
// reusing it means a picture saved from a tool is not a second kind of upload
// with a second set of behaviours to keep in step.
//
// Because it is the exception, the button says what it does, it is never the
// default action, and nothing here runs unless it is pressed.

const UPLOAD_URL = '/upload/web';

/**
 * The page's account context, or null when there is nothing to save to.
 *
 * Read from a data element the template writes, so a logged-out visitor never
 * receives a token and the bundle has nothing to decide.
 */
export function accountContext() {
    const node = document.getElementById('toolAccount');
    if (!node || node.dataset.loggedIn !== '1' || !node.dataset.csrf) return null;
    return { token: node.dataset.csrf };
}

/**
 * Upload one blob and answer with the link.
 *
 * The route replies with JSON on success and, depending on which check refused,
 * either JSON or a bare string on failure, so both shapes are handled rather
 * than assuming the happy one.
 */
async function upload(blob, filename, token) {
    const form = new FormData();
    form.append('file', blob, filename);

    const response = await fetch(UPLOAD_URL, {
        method: 'POST',
        headers: { 'X-CSRF-TOKEN': token },
        // Same-origin only, and the cookie is what identifies the account.
        credentials: 'same-origin',
        body: form,
    });

    const raw = await response.text();
    let parsed = null;
    try {
        parsed = JSON.parse(raw);
    } catch (err) {
        // A plain-text error, which some of the checks in that route still send.
    }

    if (!response.ok) {
        throw new Error((parsed && parsed.error) || raw || `Upload failed (${response.status})`);
    }
    if (!parsed || !parsed.url) {
        throw new Error('The upload finished but did not come back with a link');
    }

    return parsed.url;
}

/** One saved link, with a button that copies it. */
function linkRow(name, url) {
    const row = document.createElement('li');
    row.className = 'flex items-center gap-3 p-3';
    row.innerHTML = `
        <i class="fa-solid fa-circle-check text-emerald-500 shrink-0"></i>
        <div class="min-w-0 flex-1">
            <p data-name class="truncate text-xs font-semibold text-slate-900 dark:text-white"></p>
            <a data-link target="_blank" rel="noopener"
               class="block truncate text-xs text-blue-600 hover:underline dark:text-blue-400"></a>
        </div>
        <button type="button" data-copy
            class="shrink-0 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium bg-slate-100 dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-gray-600 transition-colors">
            <i class="fa-regular fa-copy"></i>
            <span>Copy</span>
        </button>`;

    row.querySelector('[data-name]').textContent = name;
    const link = row.querySelector('[data-link]');
    link.href = url;
    link.textContent = url;

    const copy = row.querySelector('[data-copy]');
    copy.addEventListener('click', async () => {
        try {
            await navigator.clipboard.writeText(url);
            copy.querySelector('span').textContent = 'Copied';
            setTimeout(() => { copy.querySelector('span').textContent = 'Copy'; }, 1500);
        } catch (err) {
            // Clipboard access can be refused. The link is on screen and
            // selectable either way, so this is not worth an alert.
            copy.querySelector('span').textContent = 'Press Ctrl+C';
        }
    });

    return row;
}

/**
 * Wire up a "save to my account" button.
 *
 * @param {object}   options
 * @param {Element}  options.button   The button. Hidden when there is no account.
 * @param {Element}  options.panel    Container for the resulting links.
 * @param {Element}  options.list     List the links are appended to.
 * @param {Element}  [options.error]  Where a failure is reported.
 * @param {Function} options.collect  Returns, or resolves to, [{blob, name}].
 *                                   Async because one of the tools only encodes
 *                                   its result when something asks for it.
 * @returns {boolean} Whether the button was wired at all.
 */
export function wireAccountSave({ button, panel, list, error, collect }) {
    if (!button) return false;

    const context = accountContext();
    if (!context) {
        // No account, so the button would only ever produce a login redirect.
        button.hidden = true;
        return false;
    }

    button.hidden = false;

    button.addEventListener('click', async () => {
        const original = button.innerHTML;
        button.disabled = true;
        if (error) error.hidden = true;

        let items = [];
        try {
            items = (await collect()).filter(item => item && item.blob);
        } catch (err) {
            if (error) {
                error.hidden = false;
                error.textContent = err.message || 'Could not prepare the image';
            }
            button.disabled = false;
            return;
        }

        if (!items.length) {
            button.disabled = false;
            return;
        }

        // Uploaded one at a time rather than all at once: each one is a full
        // request through the encryption and storage path, and a browser that
        // opens ten of those in parallel gets rate limited by our own route.
        let saved = 0;
        try {
            for (const item of items) {
                button.innerHTML = items.length > 1
                    ? `<i class="fa-solid fa-circle-notch fa-spin"></i> Saving ${saved + 1} of ${items.length}`
                    : '<i class="fa-solid fa-circle-notch fa-spin"></i> Saving';

                const url = await upload(item.blob, item.name, context.token);
                list.prepend(linkRow(item.name, url));
                panel.hidden = false;
                saved += 1;
            }
        } catch (err) {
            if (error) {
                error.hidden = false;
                error.textContent = saved
                    ? `${saved} saved, then: ${err.message}`
                    : err.message;
            }
        } finally {
            button.disabled = false;
            button.innerHTML = original;
        }
    });

    return true;
}
